import React, { useEffect, useMemo, useState } from 'react';
import {
  Typography,
  Paper,
  Box,
  Select,
  FormControl,
  MenuItem,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button
} from '@mui/material';

// 백업용 (옵션에 credit 누락 시)
const TWO_CREDIT_COURSES = ['로봇명사와의만남', '지능형로봇의이해', 'IntroductiontoIntelligentRobotics'];

/**
 * props
 * - standardSubject: 표준교과목명 (표시용)
 * - currentModule: 모듈명
 * - selected: 상위 선택 상태
 * - addLecture: 상위 상태 세터
 * - lectureOptions: [{ courseId, courseName, credit, isSelectable }]
 * - dense: 콤팩트 모드 여부
 */
function SubjectListCard({ standardSubject, currentModule, selected, addLecture, lectureOptions = [], dense = false }) {
  const [open, setOpen] = useState(false);
  const [pendingCourseId, setPendingCourseId] = useState(null);
  const [selectedOption, setSelectedOption] = useState(''); // 현재 그룹 내 선택된 courseId

  const isAllFalse = useMemo(() => lectureOptions.every((o) => !o.isSelectable), [lectureOptions]);

  const getCreditById = (cid) => {
    const o = lectureOptions.find((x) => x.courseId === cid);
    if (o && typeof o.credit === 'number') return o.credit;
    const name = o?.courseName ?? '';
    return TWO_CREDIT_COURSES.includes(name) ? 2 : 3;
  };

  const findCourseNameById = (cid) => lectureOptions.find((x) => x.courseId === cid)?.courseName || '';

  // 현재 그룹 내 이미 선택된 과목 동기화
  useEffect(() => {
    const inGroup = (selected.selectedLectures || []).find((lec) => lectureOptions.some((o) => o.courseId === lec.courseId));
    setSelectedOption(inGroup ? inGroup.courseId : '');
  }, [selected.selectedLectures, lectureOptions]);

  const handleConfirm = () => {
    setOpen(false);
    const credit = getCreditById(pendingCourseId);
    addLecture({
      selectedLectures: [{ courseId: pendingCourseId, courseName: findCourseNameById(pendingCourseId) }],
      module: currentModule,
      changed: !selected.changed,
      credit
    });
    setPendingCourseId(null);
  };

  const handleClose = () => {
    setOpen(false);
    setPendingCourseId(null);
  };

  const handleChange = (event) => {
    const newId = event.target.value; // courseId 또는 ''(선택 해제)

    // 다른 모듈에서 선택 중이면 확인
    if (selected.module !== '' && currentModule !== selected.module && newId) {
      setPendingCourseId(newId);
      setOpen(true);
      return;
    }

    // 이 그룹에서 이미 선택된 항목
    const existingInGroup = (selected.selectedLectures || []).find((lec) => lectureOptions.some((o) => o.courseId === lec.courseId));
    const existingId = existingInGroup?.courseId || null;
    const existingCredit = existingId ? getCreditById(existingId) : 0;

    if (newId === '') {
      // 선택 해제
      if (!existingId) {
        setSelectedOption('');
        return;
      }
      addLecture((prev) => {
        const nextLectures = prev.selectedLectures.filter((lec) => lec.courseId !== existingId);
        const nextCredit = prev.credit - existingCredit;
        setSelectedOption('');
        return nextCredit
          ? { ...prev, selectedLectures: nextLectures, credit: nextCredit }
          : { selectedLectures: [], module: '', changed: false, credit: 0 };
      });
      return;
    }

    // 신규/교체 선택
    const newCredit = getCreditById(newId);
    const creditAfter = selected.credit - existingCredit + newCredit;

    if (creditAfter > 13) {
      // eslint-disable-next-line no-alert
      alert('학점은 13학점을 초과할 수 없습니다');
      return;
    }

    addLecture((prev) => {
      const filtered = prev.selectedLectures.filter((lec) => lec.courseId !== existingId);
      return {
        ...prev,
        module: currentModule,
        selectedLectures: filtered.concat({ courseId: newId, courseName: findCourseNameById(newId) }),
        credit: prev.credit - existingCredit + newCredit
      };
    });
    setSelectedOption(newId);
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">{'다른 모듈의 과목을 선택하시나요?'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            다른 모듈 과목을 선택하면 기존 모듈에서 선택했던 과목이 취소됩니다. 계속 진행하시겠습니까?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirm} autoFocus>
            예
          </Button>
          <Button onClick={handleClose}>아니오</Button>
        </DialogActions>
      </Dialog>

      <Paper
        sx={{
          p: dense ? '4px' : '7px',
          backgroundColor: isAllFalse ? 'grey.300' : selectedOption ? 'primary.main' : 'white',
          color: isAllFalse ? 'grey.500' : selectedOption ? 'white' : 'black'
        }}
      >
        <Box margin={dense ? 0.5 : 1}>
          <Typography variant={dense ? 'body2' : 'body1'}>{standardSubject}</Typography>
        </Box>
        <FormControl fullWidth size={dense ? 'small' : 'medium'}>
          <InputLabel sx={{ paddingY: 0.25 }} id="subject-select-label">
            선택(택1)
          </InputLabel>
          <Select
            value={selectedOption}
            onChange={handleChange}
            labelId="subject-select-label"
            id="subject-select"
            label="과목 선택"
            sx={{
              backgroundColor: selectedOption ? 'white' : isAllFalse ? 'grey.300' : 'white',
              border: '1px solid',
              borderColor: 'grey.400',
              '& .MuiSelect-icon': { color: isAllFalse ? 'grey.500' : 'black' }
            }}
            disabled={isAllFalse}
          >
            <MenuItem value="">선택하지 않음</MenuItem>
            {lectureOptions.map((o) => (
              <MenuItem key={o.courseId} value={o.courseId} disabled={!o.isSelectable}>
                {o.courseName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>
    </>
  );
}

export default SubjectListCard;
